import * as React from "react";
import { Link } from "react-router-dom";

import {
  AppraisalKinds,
  ArchiveKinds,
  CloneKinds,
  ConditionKinds,
  DeclarationsKinds,
  FeeChangeKinds,
  KahunaMessageKinds,
  PointsAndCreditsKinds,
  SandboxKinds,
  SSNKinds,
  TRUSEventShape,
  FieldUpdateKinds,
  WorksheetKinds,
  ContactUpdatedKinds,
} from "web_modules/Events/types";
import { getHurdleName } from "web_modules/Events/shared";

import {
  isAppraisalKind,
  isArchivedKind,
  isCloneKind,
  isConditionKind,
  isDeclarationsKind,
  isDocmagicKind,
  isFeeChangeKind,
  isHurdleNoteKind,
  isKahunaMessageKind,
  isLobLetterKind,
  isPointsAndCreditsKind,
  isSandboxKind,
  isSSNKind,
  isFieldUpdateKind,
  isWorksheetKind,
  isContactUpdatedKind,
} from "../shared";

const styles = require("../styles.module.scss");

const ENG_SCOPE_PREFIX = "[ENG]";

const UnknownEventTitle: React.FC<{ event: TRUSEventShape }> = ({ event }) => (
  <>Unknown event: {event.kind}</>
);

interface Props {
  appId: number;
  event: TRUSEventShape;
  isPinned: boolean;
}

const AppraisalTitle: React.FC<Props> = ({ event }) => {
  const map = {
    [AppraisalKinds.ReportSent]: "Appraisal report sent",
    [AppraisalKinds.ReportDelivered]: "Appraisal report delivered",
    [AppraisalKinds.ReportRequested]: "Appraisal requested",
    [AppraisalKinds.ReportExpected]: "Appraisal expected",
    [AppraisalKinds.ReportApproved]: "Appraisal approved",
  };
  return map[event.kind] ? <>{map[event.kind]}</> : <UnknownEventTitle event={event} />;
};

const ArchivedTitle: React.FC<Props> = ({ event }) => {
  if (event.kind === ArchiveKinds.LoanArchived) {
    return <>Loan Archived</>;
  }

  if (event.kind === ArchiveKinds.LoanUnArchived) {
    return <>Loan un-archived</>;
  }
};

const CloneTitle: React.FC<Props> = ({ event }) => {
  if (event.kind === CloneKinds.Clone) {
    return <>Application cloned</>;
  }
  return <UnknownEventTitle event={event} />;
};

const ConditionTitle: React.FC<Props> = ({ appId, event }) => {
  if (event.kind === ConditionKinds.Update) {
    return (
      <Link to={`/underwriter/app/${appId}/ConditionsHurdle`} target="_blank">
        Condition updated
      </Link>
    );
  }
  return <UnknownEventTitle event={event} />;
};

const DeclarationsTitle: React.FC<Props> = ({ event }) => {
  if (event.kind === DeclarationsKinds.Update) {
    return <>Declarations updated</>;
  }
  return <UnknownEventTitle event={event} />;
};

const DocmagicTitle: React.FC<Props> = () => {
  return <>DocMagic event</>;
};

const FeeChangeTitle: React.FC<Props> = ({ appId, event }) => {
  if (event.kind === FeeChangeKinds.LoanOrigination) {
    return (
      <Link to={`/underwriter/app/${appId}/FeesHurdle`} target="_blank">
        Fee changed
      </Link>
    );
  }
  return <UnknownEventTitle event={event} />;
};

const HurdleNoteTitle: React.FC<Props> = ({ appId, event, isPinned }) => {
  const item = `${isPinned ? "Pinned " : ""}Note`;

  const hurdleName = getHurdleName(event.kind);
  if (event.author_email || event.author_first_name) {
    return (
      <>
        {event.scope === "eng" ? `${ENG_SCOPE_PREFIX} ` : ""}
        {item}
        {" on "}
        <strong>
          {hurdleName && (
            <Link to={`/underwriter/app/${appId}/${hurdleName}Hurdle`} target="_blank">
              {hurdleName}
            </Link>
          )}
          {!hurdleName && "Unknown hurdle"}
        </strong>
      </>
    );
  }

  return (
    <span className={styles.untitled}>
      {event.scope === "eng" ? `${ENG_SCOPE_PREFIX} ` : ""}
      {item}
    </span>
  );
};

const KahunaMessageTitle: React.FC<Props> = ({ event }) => {
  const map = {
    [KahunaMessageKinds.SendFailure]: "Kahuna message send failure",
    [KahunaMessageKinds.Sent]: "Kahuna message sent",
    [KahunaMessageKinds.Received]: "Kahuna message received",
  };
  if (map[event.kind]) {
    return (
      <>
        {event.scope === "eng" && `${ENG_SCOPE_PREFIX} `}
        {map[event.kind]}
      </>
    );
  }
  return <UnknownEventTitle event={event} />;
};

const LobLetterTitle: React.FC<Props> = ({ event }) => {
  return <>{event.scope === "eng" && `${ENG_SCOPE_PREFIX} `}Lob letter</>;
};

const PointsAndCreditsTitle: React.FC<Props> = ({ appId, event }) => {
  if (event.kind === PointsAndCreditsKinds.RateSelection) {
    return (
      <Link to={`/underwriter/app/${appId}/PointsAndCredits`} target="_blank">
        Points & Credits rate selection
      </Link>
    );
  }
  return <UnknownEventTitle event={event} />;
};

const SandboxTitle: React.FC<Props> = ({ event }) => {
  if (event.kind === SandboxKinds.ReworkRequest) {
    return <>Sandbox rework request</>;
  }
  return <UnknownEventTitle event={event} />;
};

const SSNTitle: React.FC<Props> = ({ appId, event }) => {
  if (event.kind === SSNKinds.Update) {
    return (
      <Link to={`/underwriter/app/${appId}/PersonalInfoHurdle`} target="_blank">
        SSN updated
      </Link>
    );
  }
  return <UnknownEventTitle event={event} />;
};

const WorksheetTitle: React.FC<Props> = ({ event }) => {
  if (event.kind === WorksheetKinds.EditEscrows) {
    return <>{ENG_SCOPE_PREFIX} Escrows worksheet edited</>;
  }
  if (event.kind === WorksheetKinds.EditPrepaids) {
    return <>{ENG_SCOPE_PREFIX} Prepaids worksheet edited</>;
  }
  return <UnknownEventTitle event={event} />;
};

const ContactAssignmentTitle: React.FC<Props> = ({ event }) => {
  if (event.kind === ContactUpdatedKinds.ContactAssigned) {
    return <>Contact assigned</>;
  }
  return <UnknownEventTitle event={event} />;
};

const FieldUpdateTitle: React.FC<Props> = ({ event }) => {
  if (event.kind === FieldUpdateKinds.FieldUpdateExpectedGrossMonthlyRentalIncome) {
    return <>Field updated: Expected gross monthly rental income updated</>;
  }
  return <UnknownEventTitle event={event} />;
};

const Title: React.FC<Props> = props => {
  const { kind } = props.event;

  if (isAppraisalKind(kind)) {
    return <AppraisalTitle {...props} />;
  }
  if (isArchivedKind(kind)) {
    return <ArchivedTitle {...props} />;
  }
  if (isCloneKind(kind)) {
    return <CloneTitle {...props} />;
  }
  if (isConditionKind(kind)) {
    return <ConditionTitle {...props} />;
  }
  if (isDeclarationsKind(kind)) {
    return <DeclarationsTitle {...props} />;
  }
  if (isDocmagicKind(kind)) {
    return <DocmagicTitle {...props} />;
  }
  if (isFeeChangeKind(kind)) {
    return <FeeChangeTitle {...props} />;
  }
  if (isHurdleNoteKind(kind)) {
    return <HurdleNoteTitle {...props} />;
  }
  if (isKahunaMessageKind(kind)) {
    return <KahunaMessageTitle {...props} />;
  }
  if (isLobLetterKind(kind)) {
    return <LobLetterTitle {...props} />;
  }
  if (isPointsAndCreditsKind(kind)) {
    return <PointsAndCreditsTitle {...props} />;
  }
  if (isSandboxKind(kind)) {
    return <SandboxTitle {...props} />;
  }
  if (isSSNKind(kind)) {
    return <SSNTitle {...props} />;
  }
  if (isWorksheetKind(kind)) {
    return <WorksheetTitle {...props} />;
  }
  if (isFieldUpdateKind(kind)) {
    return <FieldUpdateTitle {...props} />;
  }
  if (isContactUpdatedKind(kind)) {
    return <ContactAssignmentTitle {...props} />;
  }

  return <UnknownEventTitle {...props} />;
};

export default Title;
